import {cloneDeep} from 'lodash';
import {Action, Selector, State, StateContext} from '@ngxs/store';
import {Injectable} from '@angular/core';
import {BaseService, DataBaseServiceResponse} from '../../services/base-service/base.service';
import {environment} from '../../../environments/environment';
import {IRecordMedicalPersonal} from './record-medical-personal.interface';
import {IMedicalPersonalDisease} from './medical-personal-disease.interface';
import {firstValueFrom} from 'rxjs';

export interface IRMedicalPersonal {
  medicalPersonal: IRecordMedicalPersonal;
  wasDeployed?: boolean;
}

export namespace RecordMedicalPersonalAction {
  export class GetActive {
    static readonly type = '[Record] Get Record Medical Personal Action';
    constructor(
      public serviceId: string,
      public patientId: string,
      public expertId: string
    ) { }
  }

  export class PatchActive {
    static readonly type = '[Record] Patch Record Medical Personal Action';
    constructor(
      public serviceId: string,
      public patientId: string,
      public expertId: string,
      public data: Partial<IRecordMedicalPersonal>
    ) { }
  }

  export class PostDisease {
    static readonly type = '[Record] Post Medical Personal Disease Action';
    constructor(
      public serviceId: string,
      public patientId: string,
      public expertId: string,
      public data: Partial<IMedicalPersonalDisease>
    ) { }
  }

  export class DeleteDiseaseById {
    static readonly type = '[Record] Delete Medical Personal Disease Action';
    constructor(
      public serviceId: string,
      public patientId: string,
      public expertId: string,
      public diseaseId: string
    ) { }
  }

  export class ResetState {
    static readonly type = '[Record] Reset Record Medical Personal Action';
    constructor() { }
  }

  export class Deploy {
    static readonly type = '[Record] Was Deployed Record Medical Personal Action';
    constructor(public value: boolean) { }
  }
}

@State<IRMedicalPersonal>({
  name: 'MedicalPersonalState',
  defaults: {
    medicalPersonal: null,
  },
})
@Injectable()
export class MedicalPersonalState {
  constructor(
    private baseService: BaseService<any>,
  ) { }

  url = environment.SERVER_URL;

  @Selector()
  public static getRecordMedicalPersonal(
    state: IRMedicalPersonal
  ): IRecordMedicalPersonal {
    return state.medicalPersonal;
  }

  @Selector()
  public static getWasDeployed(state: IRMedicalPersonal): boolean {
    return state.wasDeployed;
  }

  @Action(RecordMedicalPersonalAction.GetActive)
  async getRecordMedicalPersonalAction(
    ctx: StateContext<IRMedicalPersonal>,
    action: RecordMedicalPersonalAction.GetActive
  ): Promise<void> {
    const response: DataBaseServiceResponse<IRecordMedicalPersonal> = await firstValueFrom(
      this.baseService.get(`${this.url}/patients/${action.patientId}/service/${action.serviceId}/records/medical-personal/active`)
    );

    if (response.error) throw new Error(response.message);

    ctx.patchState({
      medicalPersonal: response.entity,
    });
  }

  @Action(RecordMedicalPersonalAction.PatchActive)
  async patchRecordMedicalPersonalAction(
    ctx: StateContext<IRMedicalPersonal>,
    action: RecordMedicalPersonalAction.PatchActive
  ): Promise<void> {
    const response: DataBaseServiceResponse<void> = await firstValueFrom(
      this.baseService.patch(`${this.url}/patients/${action.patientId}/service/${action.serviceId}/records/medical-personal`, action.data)
    );

    if (response.error) throw new Error(response.message);
  }

  @Action(RecordMedicalPersonalAction.PostDisease)
  async postMedicalPersonalDiseaseAction(
    ctx: StateContext<IRMedicalPersonal>,
    action: RecordMedicalPersonalAction.PostDisease
  ): Promise<void> {
    const response: DataBaseServiceResponse<IMedicalPersonalDisease> = await firstValueFrom(
      this.baseService.post(`${this.url}/patients/${action.patientId}/service/${action.serviceId}/records/medical-personal/disease`, action.data)
    );

    if (response.error) throw new Error(response.message);

    let record: IRecordMedicalPersonal = cloneDeep(ctx.getState().medicalPersonal);
    if (record && record.diseases) {
      record.diseases.push(response.entity);
    } else {
      record = {
        diseases: [response.entity],
        expertId: null,
        patientId: null,
        serviceId: null,
        // Agregue estas dos propiedades porque marcaba error el tipo
        hasProsthesis: false,
        hasSurgery: false,
      };
    }

    ctx.patchState({
      medicalPersonal: record,
    });
  }

  @Action(RecordMedicalPersonalAction.DeleteDiseaseById)
  async deleteMedicalPersonalDiseaseAction(
    ctx: StateContext<IRMedicalPersonal>,
    {diseaseId, serviceId, patientId}: RecordMedicalPersonalAction.DeleteDiseaseById
  ): Promise<void> {
    const response: DataBaseServiceResponse<void> = await firstValueFrom(
      this.baseService.delete(`${this.url}/patients/${patientId}/service/${serviceId}/records/medical-personal/disease/${diseaseId}`)
    );

    if (response.error) throw new Error(response.message);
  }

  @Action(RecordMedicalPersonalAction.ResetState)
  async resetRecordMedicalPersonalAction(
    ctx: StateContext<IRMedicalPersonal>,
    _: RecordMedicalPersonalAction.ResetState
  ): Promise<void> {
    ctx.patchState({
      medicalPersonal: null,
    });
  }

  @Action(RecordMedicalPersonalAction.Deploy)
  async wasDeployedRecordMedicalPersonalAction(
    ctx: StateContext<IRMedicalPersonal>,
    action: RecordMedicalPersonalAction.Deploy
  ): Promise<void> {
    ctx.patchState({
      wasDeployed: action.value
    });
  }
}
